import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatError, windowLabel } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, CreditCard, AlertTriangle } from "lucide-react";

export default function Checkout() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [stripeLive, setStripeLive] = useState(true); // assume live until proven otherwise (avoids flashing banner)

  useEffect(() => {
    api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch((e) => {
      toast.error(formatError(e.response?.data?.detail));
    }).finally(() => setLoading(false));
    api.get("/stripe/mode").then((r) => setStripeLive(!!r.data?.live)).catch(() => {});
  }, [orderId]);

  const onPay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Failed to start checkout");
      setPaying(false);
    }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-16 text-slate-500" data-testid="loading">Loading…</div>;
  if (!order) return (
    <div className="max-w-3xl mx-auto px-4 py-16">
      <div className="card-clay p-6">
        <h1 className="font-fredoka text-2xl font-bold">Order not found</h1>
        <Link to="/schedule" className="btn-push-green inline-block mt-4 px-5 py-2.5">Schedule a pickup</Link>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <Link to="/schedule" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-800 mb-4" data-testid="back-link">
        <ArrowLeft size={16} /> Back to schedule
      </Link>
      <h1 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900" data-testid="checkout-title">Confirm & pay</h1>
      <p className="text-slate-600 mt-1">Review your pickup details. Secure Stripe Checkout — we never store your card.</p>

      {!stripeLive && (
        <div className="card-clay p-3 mt-4 bg-amber-50 border-amber-200 flex items-center gap-2" data-testid="stripe-test-banner">
          <AlertTriangle className="text-amber-600 flex-shrink-0" size={16} strokeWidth={2.5}/>
          <div className="text-xs text-amber-900">
            <b>TEST MODE</b> — this checkout uses a Stripe test key. Use test card <code className="font-mono">4242 4242 4242 4242</code>. Real cards won't be charged. (This banner only appears in preview/staging.)
          </div>
        </div>
      )}

      <div className="card-clay p-6 mt-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="font-fredoka text-xl font-bold text-slate-900">Order summary</div>
          <StatusPill status={order.payment_status} />
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
          <Row label="Name" value={order.name} />
          <Row label="Email" value={order.email} />
          <Row label="Community" value={order.community} />
          <Row label="Building / Unit" value={`${order.building} / ${order.unit}`} />
          <Row label="Window" value={windowLabel(order.window)} />
          <Row label="Type" value={order.order_type === "recurring" ? "Weekly recurring" : "One-time"} />
          <Row label="Bags" value={order.bags} />
          {order.monthly_cleaning && <Row label="Add-on" value="Monthly bin cleaning" />}
          {order.location_instructions && <Row label="Instructions" value={order.location_instructions} />}
        </div>
      </div>

      <div className="card-clay p-6 mt-4">
        <div className="font-fredoka text-xl font-bold text-slate-900 mb-3">Charges</div>
        <Line label="Pickup base" value={order.base_amount} />
        {order.cleaning_amount > 0 && <Line label="Monthly cleaning add-on" value={order.cleaning_amount} />}
        {order.tip_amount > 0 && <Line label="Tip" value={order.tip_amount} />}
        <div className="border-t-2 border-slate-100 mt-3 pt-3 flex items-center justify-between">
          <div className="font-fredoka font-bold text-lg">Total</div>
          <div className="font-fredoka font-bold text-2xl text-slate-900" data-testid="checkout-total">${order.total_amount.toFixed(2)}</div>
        </div>
      </div>

      {order.payment_status === "paid" ? (
        <div className="card-clay p-6 mt-4 bg-green-50 border-green-200" data-testid="already-paid">
          <div className="font-fredoka font-bold text-green-800">This order is paid. You're all set!</div>
          <Link to="/dashboard" className="btn-push-green inline-block mt-3 px-5 py-2.5">Go to My Pickups</Link>
        </div>
      ) : (
        <button onClick={onPay} disabled={paying} className="btn-push-green w-full mt-6 py-4 inline-flex items-center justify-center gap-2 text-base" data-testid="pay-now-btn">
          <CreditCard size={18} strokeWidth={2.5}/> {paying ? "Redirecting…" : `Pay $${order.total_amount.toFixed(2)} with Stripe`}
        </button>
      )}
      <div className="mt-3 text-xs text-slate-500 flex items-center gap-2 justify-center">
        <ShieldCheck size={14} className="text-green-600" strokeWidth={2.5}/> Card details are processed by Stripe. We never store them.
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="rounded-xl bg-slate-50 p-3">
    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="font-semibold text-slate-800 mt-0.5">{value}</div>
  </div>
);
const Line = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5 text-sm">
    <div className="text-slate-600">{label}</div>
    <div className="font-semibold text-slate-800">${Number(value).toFixed(2)}</div>
  </div>
);
const StatusPill = ({ status }) => {
  const map = {
    paid: "bg-green-100 text-green-800",
    pending: "bg-slate-100 text-slate-700",
    failed: "bg-red-100 text-red-800",
    refunded: "bg-amber-100 text-amber-800",
    canceled: "bg-slate-200 text-slate-700",
  };
  return (
    <span className={`rounded-full text-xs font-bold uppercase tracking-wider px-3 py-1 ${map[status] || "bg-slate-100 text-slate-700"}`}>
      {status}
    </span>
  );
};
